/**
 * Cookie helpers for Cognito auth tokens.
 *
 * Tokens are written to cookies on the parent domain `.3mote.com` so
 * that the SPA at `aeeiee-staging.3mote.com` and the API at
 * `api-staging.3mote.com` share the auth session. This is the
 * cross-subdomain sharing point the entire architecture depends on.
 *
 * Phase 1 stores all three tokens (access, ID, refresh) as regular
 * (non-HttpOnly) cookies because we own both ends of the conversation.
 * A future phase may switch the refresh token to HttpOnly via a thin
 * server roundtrip — see Phase 1 PoC notes in the plan.
 */

const COOKIE_DOMAIN = '.3mote.com'

const ACCESS_TOKEN_COOKIE_BASE = 'next-access-token'
const ID_TOKEN_COOKIE_BASE = 'next-id-token'
const REFRESH_TOKEN_COOKIE_BASE = 'next-refresh-token'

/**
 * Staging and prod share the `.3mote.com` parent cookie domain but use
 * DIFFERENT Cognito pools — with a single set of cookie names, signing
 * in on one stage overwrites the other stage's tokens, the other
 * stage's next request carries a wrong-issuer JWT, its refresh feeds
 * the wrong pool's refresh token to Cognito, and the 401 chain logs
 * the user out. Stage-suffixed names give each stage its own cookie
 * slot while keeping cross-subdomain sharing WITHIN a stage intact.
 *
 * Staging hosts carry a `-staging` suffix on the first label
 * (`aeeiee-staging.3mote.com`); prod hosts are suffix-less and keep
 * the bare names. Non-`.3mote.com` hosts (localhost) also keep the
 * bare names — they use host-only cookies, so no cross-stage
 * collision is possible there. A tenant slug that itself ends in
 * `-staging` is rejected at signup (see the host-parser pitfall), so
 * the label test can't misfire on prod.
 */
function cookieStageSuffix(): string {
  if (typeof window === 'undefined') return ''
  const hostname = window.location.hostname
  if (!hostname.endsWith('.3mote.com')) return ''
  const firstLabel = hostname.split('.')[0] ?? ''
  return firstLabel.endsWith('-staging') ? '-staging' : ''
}

function tokenCookieNames(): readonly [access: string, id: string, refresh: string] {
  const suffix = cookieStageSuffix()
  return [
    `${ACCESS_TOKEN_COOKIE_BASE}${suffix}`,
    `${ID_TOKEN_COOKIE_BASE}${suffix}`,
    `${REFRESH_TOKEN_COOKIE_BASE}${suffix}`,
  ]
}

interface SetTokensInput {
  accessToken: string
  idToken: string
  refreshToken: string
}

interface SetCookieOptions {
  /** Lifetime in seconds. Defaults to 30 days. */
  maxAge?: number
}

/**
 * Should we set the Secure attribute? `true` everywhere except on plain
 * http://localhost during dev — browsers reject Secure cookies sent over
 * http for security reasons.
 */
function shouldUseSecure(): boolean {
  if (typeof window === 'undefined') return true
  return window.location.protocol !== 'http:'
}

/**
 * Use the parent domain `.3mote.com` only when running on a `.3mote.com`
 * host. On localhost or any other host, fall back to a host-only cookie
 * (omit Domain) so the browser still accepts it.
 */
function shouldUseParentDomain(): boolean {
  if (typeof window === 'undefined') return true
  return window.location.hostname.endsWith('.3mote.com')
}

function writeCookie(name: string, value: string, options: SetCookieOptions = {}): void {
  if (typeof document === 'undefined') return
  const maxAge = options.maxAge ?? 60 * 60 * 24 * 30 // 30 days
  const parts: string[] = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
  ]
  if (shouldUseParentDomain()) parts.push(`Domain=${COOKIE_DOMAIN}`)
  if (shouldUseSecure()) parts.push('Secure')
  document.cookie = parts.join('; ')
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  const parts: string[] = [`${name}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax']
  if (shouldUseParentDomain()) parts.push(`Domain=${COOKIE_DOMAIN}`)
  if (shouldUseSecure()) parts.push('Secure')
  document.cookie = parts.join('; ')
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (const raw of cookies) {
    const eq = raw.indexOf('=')
    if (eq === -1) continue
    const cookieName = raw.slice(0, eq)
    if (cookieName === name) {
      return decodeURIComponent(raw.slice(eq + 1))
    }
  }
  return null
}

export function setTokens({ accessToken, idToken, refreshToken }: SetTokensInput): void {
  const [accessName, idName, refreshName] = tokenCookieNames()
  writeCookie(accessName, accessToken)
  writeCookie(idName, idToken)
  // Refresh token gets a longer lifetime since the user pool's own
  // refresh-token validity is the real ceiling (default 30 days).
  writeCookie(refreshName, refreshToken, { maxAge: 60 * 60 * 24 * 30 })
}

export function getAccessToken(): string | null {
  return readCookie(tokenCookieNames()[0])
}

export function getIdToken(): string | null {
  return readCookie(tokenCookieNames()[1])
}

export function getRefreshToken(): string | null {
  return readCookie(tokenCookieNames()[2])
}

export function clearTokens(): void {
  for (const name of tokenCookieNames()) {
    deleteCookie(name)
  }
}
